/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, Filter, CheckCircle, XCircle, Clock, FileText, Eye, List, CalendarIcon, ChevronLeft, ChevronRight, Download, AlertTriangle, DoorOpen, Printer } from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isSameDay, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'In Attesa', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]', color: '#F59E0B' },
  confirmed: { label: 'Confermato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]', color: '#1E7A58' },
  rejected: { label: 'Rifiutato', className: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]', color: '#E5484D' },
  cancelled: { label: 'Annullato', className: 'bg-slate-500 text-white', color: '#64748B' },
  completed: { label: 'Completato', className: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]', color: '#1677C7' },
};

const PAYMENT_MAP = {
  paid: { label: 'Pagato', className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' },
  pending: { label: 'Da pagare', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
  expired: { label: 'Da pagare', className: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
  failed: { label: 'Pag. fallito', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
};

const paymentBadge = (a, withAmount = false) => {
  const p = PAYMENT_MAP[a?.payment_status];
  if (!p) return null;
  const amount = withAmount && a.payment_amount ? ` ${Number(a.payment_amount).toFixed(2)} EUR` : '';
  const tipo = a.payment_type === 'deposit' ? ' (acconto)' : '';
  return (
    <Badge className={`text-[10px] px-1.5 py-0 ${p.className}`} data-testid={`appt-payment-badge-${a.id}`}>
      {p.label}{amount}{withAmount ? tipo : ''}
    </Badge>
  );
};

const HOURS = Array.from({length: 11}, (_, i) => `${(i + 8).toString().padStart(2, '0')}:00`);

export default function GestioneAppuntamenti() {
  const {} = useAuth(); // cookies sent automatically

  const [appointments, setAppointments] = useState([]);
  const [calendarAppts, setCalendarAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Filtri iniziali da URL (deep-link dalla Dashboard: ?status=&doctor=&view=&appt=)
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [doctorFilter, setDoctorFilter] = useState(searchParams.get('doctor') || 'all');
  const openApptRef = useRef(searchParams.get('appt') || null);
  const [detailAppt, setDetailAppt] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showAllStudios, setShowAllStudios] = useState(false);

  // Reset del filtro studi quando si apre un nuovo dettaglio
  useEffect(() => { setShowAllStudios(false); }, [detailAppt?.id]);
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'list');
  const [selectedIds, setSelectedIds] = useState([]);
  const [calendarWeek, setCalendarWeek] = useState(new Date());
  const [studioDay, setStudioDay] = useState(new Date());
  const [studioAppts, setStudioAppts] = useState([]);
  const [printingAgenda, setPrintingAgenda] = useState(false);

  const handleAgendaPdf = async () => {
    setPrintingAgenda(true);
    try {
      const dayStr = format(studioDay, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/appointments/agenda-pdf`, {
        params: { data: dayStr },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `agenda_${dayStr}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Agenda PDF scaricata');
    } catch {
      toast.error('Errore nella generazione del PDF');
    } finally {
      setPrintingAgenda(false);
    }
  };

  const weekStart = startOfWeek(calendarWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(calendarWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({length: 6}, (_, i) => addDays(weekStart, i)); // Mon-Sat

  const fetchListData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, dRes] = await Promise.all([
        axios.get(`${API}/appointments`, { params: { search: search || undefined, status: statusFilter !== 'all' ? statusFilter : undefined, doctor_id: doctorFilter !== 'all' ? doctorFilter : undefined } }),
        axios.get(`${API}/doctors?active_only=false`)
      ]);
      setAppointments(aRes.data);
      setDoctors(dRes.data);
      // Deep-link dalla Dashboard: apri il dettaglio dell'appuntamento richiesto (una sola volta)
      if (openApptRef.current) {
        const target = aRes.data.find(a => a.id === openApptRef.current);
        openApptRef.current = null;
        if (target) setDetailAppt(target);
      }
      try {
        const sRes = await axios.get(`${API}/studios`);
        setStudios(sRes.data);
      } catch (e) { logger.warn('Studi (opzionali) non caricati:', e?.message); }
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, [search, statusFilter, doctorFilter]);

  const fetchCalendarData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/appointments/calendar`, {
        params: {
          data_from: format(weekStart, 'yyyy-MM-dd'),
          data_to: format(weekEnd, 'yyyy-MM-dd'),
          doctor_id: doctorFilter !== 'all' ? doctorFilter : undefined
        }
      });
      setCalendarAppts(res.data);
    } catch (err) { logger.error("Fetch error:", err); }
  }, [weekStart, weekEnd, doctorFilter]);

  const fetchStudioDayData = useCallback(async () => {
    try {
      const dayStr = format(studioDay, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/appointments/calendar`, {
        params: {
          data_from: dayStr,
          data_to: dayStr,
          doctor_id: doctorFilter !== 'all' ? doctorFilter : undefined
        }
      });
      // Solo appuntamenti "attivi": gli annullati/rifiutati non occupano la stanza
      setStudioAppts(res.data.filter(a => ['pending', 'confirmed', 'completed'].includes(a.status)));
    } catch (err) { logger.error("Fetch error:", err); }
  }, [studioDay, doctorFilter]);

  useEffect(() => {
    fetchListData();
  }, [statusFilter, doctorFilter, fetchListData]);

  useEffect(() => {
    if (viewMode === 'calendar') fetchCalendarData();
  }, [viewMode, calendarWeek, doctorFilter, fetchCalendarData]);

  useEffect(() => {
    if (viewMode === 'studios') fetchStudioDayData();
  }, [viewMode, studioDay, doctorFilter, fetchStudioDayData]);

  const refreshActiveView = useCallback(() => {
    if (viewMode === 'calendar') fetchCalendarData();
    if (viewMode === 'studios') fetchStudioDayData();
  }, [viewMode, fetchCalendarData, fetchStudioDayData]);

  const handleSearch = (e) => { e.preventDefault(); fetchListData(); };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}/status`, { status }, {});
      toast.success(`Stato aggiornato: ${STATUS_MAP[status]?.label}`);
      fetchListData();
      refreshActiveView();
      setDetailAppt(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const saveNotes = async (id) => {
    try {
      await axios.put(`${API}/appointments/${id}/notes`, { note_operatore: noteText }, {});
      toast.success('Note salvate');
      fetchListData();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const studioNumLabel = (s) => {
    if (!s.numero) return s.nome;
    return s.numero.toLowerCase().startsWith('studio') ? s.numero : `Studio ${s.numero}`;
  };
  const studioLabel = (s) => (s.numero ? `${studioNumLabel(s)}${s.nome && s.nome !== s.numero ? ` - ${s.nome}` : ''}` : s.nome);

  // ── Compatibilità attrezzature: studio compatibile se ha TUTTE le attrezzature richieste dal medico ──
  const requiredEquip = detailAppt
    ? (doctors.find(d => d.id === detailAppt.doctor_id)?.attrezzature_richieste || [])
    : [];
  const studiosWithCompat = studios.map(s => {
    const have = (s.attrezzature || []).map(t => t.toLowerCase());
    const missing = requiredEquip.filter(r => !have.includes(r.toLowerCase()));
    return { ...s, _missing: missing, _compatible: requiredEquip.length > 0 && missing.length === 0 };
  }).sort((a, b) => {
    if (requiredEquip.length === 0) return 0;
    return (a._missing.length - b._missing.length);
  });
  const selectedStudioCompat = detailAppt?.studio_id
    ? studiosWithCompat.find(s => s.id === detailAppt.studio_id)
    : null;

  // Filtro: di default mostra SOLO gli studi compatibili (se il medico ha requisiti).
  // Lo studio già assegnato resta sempre visibile. Toggle per mostrare tutti.
  const compatibleCount = studiosWithCompat.filter(s => s._compatible).length;
  const visibleStudios = (requiredEquip.length === 0 || showAllStudios)
    ? studiosWithCompat
    : studiosWithCompat.filter(s => s._compatible || s.id === detailAppt?.studio_id);
  const hiddenCount = studiosWithCompat.length - visibleStudios.length;

  const apptStudioLabel = (a) => {
    if (!a.studio_id) return null;
    return a.studio_numero ? `Studio ${a.studio_numero} - ${a.studio_nome}` : a.studio_nome;
  };

  const assignStudio = async (id, studioId) => {
    try {
      const res = await axios.put(`${API}/appointments/${id}/studio`, { studio_id: studioId || null }, {});
      toast.success(studioId ? 'Studio assegnato' : 'Studio rimosso');
      const patch = { studio_id: res.data.studio_id, studio_nome: res.data.studio_nome, studio_numero: res.data.studio_numero };
      setDetailAppt(prev => (prev && prev.id === id ? { ...prev, ...patch } : prev));
      setAppointments(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));
      refreshActiveView();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'assegnazione dello studio');
    }
  };

  const handleBulkConfirm = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.put(`${API}/appointments/bulk-confirm`, selectedIds, {});
      toast.success(`${res.data.confirmed} appuntamenti confermati`);
      setSelectedIds([]);
      fetchListData();
      refreshActiveView();
    } catch { toast.error('Errore'); }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/appointments/export`, {
        params: { status: statusFilter !== 'all' ? statusFilter : undefined }
      });
      const data = res.data;
      if (data.length === 0) { toast.info('Nessun dato da esportare'); return; }
      const csvHeaders = 'Data,Ora,Paziente,Email,Telefono,Servizio,Dottore,Stato,Fonte\n';
      const csvRows = data.map(a =>
        `${a.data},${a.ora},${a.patient_nome} ${a.patient_cognome},${a.patient_email || ''},${a.patient_telefono || ''},${a.service_nome},Dott. ${a.doctor_cognome},${a.status},${a.source || 'patient'}`
      ).join('\n');
      const blob = new Blob([csvHeaders + csvRows], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `appuntamenti_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();
      toast.success('File CSV scaricato');
    } catch { toast.error('Errore nell\'esportazione'); }
  };

  const toggleSelectAll = () => {
    const pendingIds = appointments.filter(a => a.status === 'pending').map(a => a.id);
    setSelectedIds(prev => prev.length === pendingIds.length ? [] : pendingIds);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const getApptsForDayHour = (day, hour) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return calendarAppts.filter(a => a.data === dayStr && a.ora?.startsWith(hour.split(':')[0]));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <PageTitle title="Gestione Appuntamenti" info="Visualizza e gestisci tutti gli appuntamenti: filtra per stato, medico e periodo; conferma, rifiuta o annulla." />
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList>
              <TabsTrigger value="list"><List size={14} className="mr-1" /> Lista</TabsTrigger>
              <TabsTrigger value="calendar"><CalendarIcon size={14} className="mr-1" /> Calendario</TabsTrigger>
              <TabsTrigger value="studios" data-testid="view-studios-tab"><DoorOpen size={14} className="mr-1" /> Studi</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Cerca paziente..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="appointments-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="pending">In Attesa</SelectItem>
                <SelectItem value="confirmed">Confermati</SelectItem>
                <SelectItem value="rejected">Rifiutati</SelectItem>
                <SelectItem value="cancelled">Annullati</SelectItem>
                <SelectItem value="completed">Completati</SelectItem>
              </SelectContent>
            </Select>
            <Select value={doctorFilter} onValueChange={setDoctorFilter}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Dottore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i dottori</SelectItem>
                {doctors.map(d => (<SelectItem key={d.id} value={d.id}>Dott. {d.cognome}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline"><Filter size={14} className="mr-1" /> Filtra</Button>
            <Button type="button" variant="outline" onClick={handleExportCSV}><Download size={14} className="mr-1" /> CSV</Button>
          </form>
          {selectedIds.length > 0 && (
            <div className="mt-3 flex items-center gap-3 bg-[hsl(var(--accent))] rounded-lg px-4 py-2">
              <span className="text-sm font-medium">{selectedIds.length} selezionati</span>
              <Button size="sm" onClick={handleBulkConfirm} className="bg-green-600 hover:bg-green-700 text-white h-7">
                <CheckCircle size={12} className="mr-1" /> Conferma Selezionati
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-7">Deseleziona</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
            ) : appointments.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">Nessun appuntamento trovato</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={selectedIds.length > 0 && selectedIds.length === appointments.filter(a => a.status === 'pending').length} onCheckedChange={toggleSelectAll} />
                      </TableHead>
                      <TableHead>Paziente</TableHead>
                      <TableHead>Servizio</TableHead>
                      <TableHead>Dottore</TableHead>
                      <TableHead>Studio</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Ora</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {appointments.map(a => (
                      <TableRow key={a.id} className="cursor-pointer hover:bg-[hsl(var(--secondary))]" data-testid="backoffice-appointments-row">
                        <TableCell>
                          {a.status === 'pending' && <Checkbox checked={selectedIds.includes(a.id)} onCheckedChange={() => toggleSelect(a.id)} />}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {a.patient_nome} {a.patient_cognome}
                            {a.source === 'guest' && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Ospite</Badge>}
                            {a.source === 'whatsapp' && <Badge className="text-[10px] px-1.5 py-0 bg-[#238A92]/15 text-[#0F5E66] hover:bg-[#238A92]/15" data-testid={`appt-whatsapp-badge-${a.id}`}>WhatsApp</Badge>}
                            {a.source === 'web' && <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-800 hover:bg-violet-100" data-testid={`appt-webchat-badge-${a.id}`}>Chat sito</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{a.service_nome}</TableCell>
                        <TableCell>Dott. {a.doctor_cognome}</TableCell>
                        <TableCell>
                          {a.studio_id
                            ? <Badge variant="outline" className="text-[11px]">{a.studio_numero ? `Studio ${a.studio_numero}` : a.studio_nome}</Badge>
                            : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                        <TableCell>{a.data}</TableCell>
                        <TableCell>{a.ora}</TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            <Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge>
                            {paymentBadge(a)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {a.status === 'pending' && (
                              <>
                                <Button size="sm" variant="ghost" className="text-green-600 h-8 px-2" onClick={() => updateStatus(a.id, 'confirmed')} title="Conferma"><CheckCircle size={16} /></Button>
                                <Button size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => updateStatus(a.id, 'rejected')} title="Rifiuta"><XCircle size={16} /></Button>
                              </>
                            )}
                            {a.status === 'confirmed' && (
                              <Button size="sm" variant="ghost" className="text-blue-600 h-8 px-2" onClick={() => updateStatus(a.id, 'completed')} title="Completa"><CheckCircle size={16} /></Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setDetailAppt(a); setNoteText(a.note_operatore || ''); }} title="Dettagli"><Eye size={16} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CALENDAR VIEW */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setCalendarWeek(prev => addDays(prev, -7))}>
                <ChevronLeft size={16} />
              </Button>
              <CardTitle className="text-base" style={{fontFamily:'Space Grotesk'}}>
                {format(weekStart, 'dd MMM', {locale: it})} - {format(addDays(weekStart, 5), 'dd MMM yyyy', {locale: it})}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setCalendarWeek(new Date())}>Oggi</Button>
                <Button variant="ghost" size="sm" onClick={() => setCalendarWeek(prev => addDays(prev, 7))}>
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Day headers */}
                <div className="grid grid-cols-[70px_repeat(6,1fr)] border-b">
                  <div className="p-2 text-xs text-muted-foreground"></div>
                  {weekDays.map(day => (
                    <div key={day.toISOString()} className={`p-2 text-center border-l ${isSameDay(day, new Date()) ? 'bg-[#238A92]/5' : ''}`}>
                      <p className="text-xs text-muted-foreground">{format(day, 'EEE', {locale: it})}</p>
                      <p className={`text-sm font-semibold ${isSameDay(day, new Date()) ? 'text-[#238A92]' : ''}`}>{format(day, 'dd')}</p>
                    </div>
                  ))}
                </div>
                {/* Time slots */}
                {HOURS.map(hour => (
                  <div key={hour} className="grid grid-cols-[70px_repeat(6,1fr)] border-b min-h-[60px]">
                    <div className="p-2 text-xs text-muted-foreground border-r flex items-start justify-end pr-3 pt-1">{hour}</div>
                    {weekDays.map(day => {
                      const dayAppts = getApptsForDayHour(day, hour);
                      return (
                        <div key={day.toISOString()} className={`p-1 border-l min-h-[60px] ${isSameDay(day, new Date()) ? 'bg-[#238A92]/5' : ''}`}>
                          {dayAppts.map(a => (
                            <button
                              key={a.id}
                              onClick={() => { setDetailAppt(a); setNoteText(a.note_operatore || ''); }}
                              className={`w-full text-left p-1.5 rounded text-[11px] leading-tight mb-0.5 border-l-2 ${
                                a.status === 'confirmed' ? 'bg-green-50 border-green-500 text-green-800' :
                                a.status === 'pending' ? 'bg-amber-50 border-amber-500 text-amber-800' :
                                a.status === 'completed' ? 'bg-blue-50 border-blue-500 text-blue-800' :
                                'bg-gray-50 border-gray-400 text-gray-600'
                              }`}
                            >
                              <p className="font-medium truncate">{a.ora} {a.patient_cognome}</p>
                              <p className="truncate opacity-75">{a.service_nome}</p>
                              {a.studio_id && (
                                <p className="truncate opacity-75 font-medium">{a.studio_numero ? `Studio ${a.studio_numero}` : a.studio_nome}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STUDIOS DAY VIEW — griglia giornaliera per stanza */}
      {viewMode === 'studios' && (
        <Card data-testid="studios-day-view">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStudioDay(prev => addDays(prev, -1))} data-testid="studios-prev-day">
                <ChevronLeft size={16} />
              </Button>
              <CardTitle className="text-base capitalize" style={{fontFamily:'Space Grotesk'}}>
                {format(studioDay, 'EEEE d MMMM yyyy', {locale: it})}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAgendaPdf}
                  disabled={printingAgenda}
                  data-testid="agenda-pdf-button"
                >
                  <Printer size={14} className="mr-1" /> {printingAgenda ? 'Generazione...' : 'Stampa PDF'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setStudioDay(new Date())}>Oggi</Button>
                <Button variant="ghost" size="sm" onClick={() => setStudioDay(prev => addDays(prev, 1))} data-testid="studios-next-day">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {studios.length === 0 ? (
              <div className="p-12 text-center">
                <DoorOpen size={40} className="mx-auto text-[#238A92] mb-3" />
                <p className="text-lg font-semibold" style={{fontFamily:'Space Grotesk'}}>Nessuno studio configurato</p>
                <p className="text-sm text-muted-foreground mt-1">Aggiungi le stanze dalla sezione "Studi" del backoffice.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div style={{ minWidth: `${90 + (studios.length + 1) * 170}px` }}>
                  {/* Intestazioni colonne: una per studio + "Senza studio" */}
                  <div className="grid border-b" style={{ gridTemplateColumns: `90px repeat(${studios.length + 1}, 1fr)` }}>
                    <div className="p-2"></div>
                    {studios.map(s => {
                      const count = studioAppts.filter(a => a.studio_id === s.id).length;
                      return (
                        <div key={s.id} className="p-2.5 text-center border-l" data-testid={`studio-column-${s.id}`}>
                          <p className="text-sm font-semibold truncate">{studioNumLabel(s)}</p>
                          {count === 0 ? (
                            <Badge className="mt-1 text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Libero tutto il giorno</Badge>
                          ) : (
                            <Badge variant="secondary" className="mt-1 text-[10px]">{count} {count === 1 ? 'appuntamento' : 'appuntamenti'}</Badge>
                          )}
                        </div>
                      );
                    })}
                    <div className="p-2.5 text-center border-l bg-[hsl(var(--secondary))]/50">
                      <p className="text-sm font-semibold text-muted-foreground">Senza studio</p>
                      <Badge variant="outline" className="mt-1 text-[10px]">{studioAppts.filter(a => !a.studio_id).length} da assegnare</Badge>
                    </div>
                  </div>
                  {/* Righe orarie */}
                  {HOURS.map(hour => (
                    <div key={hour} className="grid border-b min-h-[56px]" style={{ gridTemplateColumns: `90px repeat(${studios.length + 1}, 1fr)` }}>
                      <div className="p-2 text-xs text-muted-foreground border-r flex items-start justify-end pr-3 pt-1.5">{hour}</div>
                      {studios.map(s => {
                        const cellAppts = studioAppts.filter(a => a.studio_id === s.id && a.ora?.startsWith(hour.split(':')[0]));
                        return (
                          <div key={s.id} className={`p-1 border-l min-h-[56px] ${cellAppts.length === 0 ? 'bg-emerald-50/40' : ''}`}>
                            {cellAppts.map(a => (
                              <button
                                key={a.id}
                                onClick={() => { setDetailAppt(a); setNoteText(a.note_operatore || ''); }}
                                data-testid={`studio-cell-appt-${a.id}`}
                                className={`w-full text-left p-1.5 rounded text-[11px] leading-tight mb-0.5 border-l-2 transition-opacity duration-150 hover:opacity-80 ${
                                  a.status === 'confirmed' ? 'bg-green-50 border-green-500 text-green-800' :
                                  a.status === 'pending' ? 'bg-amber-50 border-amber-500 text-amber-800' :
                                  'bg-blue-50 border-blue-500 text-blue-800'
                                }`}
                              >
                                <p className="font-medium truncate">{a.ora} {a.patient_cognome}</p>
                                <p className="truncate opacity-75">Dott. {a.doctor_cognome}</p>
                              </button>
                            ))}
                          </div>
                        );
                      })}
                      {/* Colonna "Senza studio" */}
                      <div className="p-1 border-l min-h-[56px] bg-[hsl(var(--secondary))]/30">
                        {studioAppts.filter(a => !a.studio_id && a.ora?.startsWith(hour.split(':')[0])).map(a => (
                          <button
                            key={a.id}
                            onClick={() => { setDetailAppt(a); setNoteText(a.note_operatore || ''); }}
                            data-testid={`unassigned-cell-appt-${a.id}`}
                            className="w-full text-left p-1.5 rounded text-[11px] leading-tight mb-0.5 border-l-2 border-dashed border-slate-400 bg-white text-slate-700 transition-opacity duration-150 hover:opacity-80"
                            title="Clicca per assegnare uno studio"
                          >
                            <p className="font-medium truncate">{a.ora} {a.patient_cognome}</p>
                            <p className="truncate opacity-75">Dott. {a.doctor_cognome}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {studios.length > 0 && (
              <div className="flex items-center gap-4 px-4 py-3 text-xs text-muted-foreground border-t flex-wrap">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-50 border border-emerald-200"></span> Libero</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-50 border-l-2 border-green-500"></span> Confermato</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border-l-2 border-amber-500"></span> In attesa</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-white border-l-2 border-dashed border-slate-400"></span> Senza studio (clicca per assegnare)</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailAppt} onOpenChange={() => setDetailAppt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Dettaglio Appuntamento</DialogTitle>
          </DialogHeader>
          {detailAppt && (
            <div className="space-y-4">
              <div className="bg-[hsl(var(--secondary))] rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Paziente</span><span className="font-medium">{detailAppt.patient_nome} {detailAppt.patient_cognome} {detailAppt.source === 'guest' && <Badge variant="secondary" className="text-[10px] ml-1">Ospite</Badge>}{detailAppt.source === 'whatsapp' && <Badge className="text-[10px] ml-1 bg-[#238A92]/15 text-[#0F5E66] hover:bg-[#238A92]/15">WhatsApp</Badge>}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{detailAppt.patient_email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Telefono</span><span>{detailAppt.patient_telefono || 'N/D'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Servizio</span><span className="font-medium">{detailAppt.service_nome}</span></div>
                <div className="flex justify-between" data-testid="appt-detail-source-row">
                  <span className="text-muted-foreground">Prenotato via</span>
                  <span>
                    {detailAppt.source === 'web' && <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">Chat del sito (AI)</Badge>}
                    {detailAppt.source === 'whatsapp' && <Badge className="bg-[#238A92]/15 text-[#0F5E66] hover:bg-[#238A92]/15">WhatsApp (AI)</Badge>}
                    {detailAppt.source === 'guest' && <Badge variant="secondary">Ospite</Badge>}
                    {(!detailAppt.source || detailAppt.source === 'patient') && <span className="text-sm">Sito / Backoffice</span>}
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-muted-foreground">Dottore</span><span>Dott. {detailAppt.doctor_nome} {detailAppt.doctor_cognome}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Data e Ora</span><span className="font-medium">{detailAppt.data} alle {detailAppt.ora}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Durata</span><span>{detailAppt.durata_minuti} min</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Stato</span><Badge className={STATUS_MAP[detailAppt.status]?.className}>{STATUS_MAP[detailAppt.status]?.label}</Badge></div>
                {PAYMENT_MAP[detailAppt.payment_status] && (
                  <div className="flex justify-between" data-testid="appt-detail-payment-row"><span className="text-muted-foreground">Pagamento</span><span>{paymentBadge(detailAppt, true)}</span></div>
                )}
                {detailAppt.note_paziente && (
                  <div className="pt-2 border-t"><span className="text-muted-foreground">Note paziente:</span><p className="mt-1">{detailAppt.note_paziente}</p></div>
                )}
              </div>
              <div>
                <Label>Studio (stanza)</Label>
                {requiredEquip.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center flex-wrap gap-1">
                    Il Dott. {detailAppt.doctor_cognome} richiede:
                    {requiredEquip.map(t => <Badge key={t} variant="outline" className="text-[10px] font-normal">{t}</Badge>)}
                  </p>
                )}
                <Select
                  value={detailAppt.studio_id || 'none'}
                  onValueChange={(v) => assignStudio(detailAppt.id, v === 'none' ? null : v)}
                >
                  <SelectTrigger className="mt-1" data-testid="appointment-studio-select">
                    <SelectValue placeholder="Assegna uno studio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno studio</SelectItem>
                    {visibleStudios.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex items-center gap-2">
                          {studioLabel(s)}
                          {s._compatible && (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">Compatibile</span>
                          )}
                          {requiredEquip.length > 0 && !s._compatible && (
                            <span className="text-[10px] text-amber-700">manca: {s._missing.join(', ')}</span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {requiredEquip.length > 0 && studios.length > 0 && (
                  <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-muted-foreground" data-testid="studio-compat-count">
                      {compatibleCount === 0
                        ? 'Nessuno studio ha tutte le attrezzature richieste.'
                        : `${compatibleCount} ${compatibleCount === 1 ? 'studio compatibile' : 'studi compatibili'} su ${studiosWithCompat.length}.`}
                    </p>
                    {(hiddenCount > 0 || showAllStudios) && (
                      <button
                        type="button"
                        onClick={() => setShowAllStudios(v => !v)}
                        className="text-xs font-medium text-[#238A92] hover:text-[#0F5E66] hover:underline transition-colors duration-150"
                        data-testid="studio-show-all-toggle"
                      >
                        {showAllStudios ? 'Mostra solo compatibili' : `Mostra tutti gli studi (+${hiddenCount})`}
                      </button>
                    )}
                  </div>
                )}
                {selectedStudioCompat && requiredEquip.length > 0 && selectedStudioCompat._missing.length > 0 && (
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-start gap-2" data-testid="studio-incompat-warning">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      Questo studio non ha tutte le attrezzature richieste dal medico. Mancano: <strong>{selectedStudioCompat._missing.join(', ')}</strong>
                    </p>
                  </div>
                )}
                {studios.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Nessuno studio disponibile. Aggiungili dalla sezione "Studi".</p>
                )}
              </div>
              <div>
                <Label>Note operatore</Label>
                <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Aggiungi note..." className="mt-1" />
                <Button size="sm" onClick={() => saveNotes(detailAppt.id)} className="mt-2 bg-[#238A92] hover:bg-[#0F5E66] text-white">
                  <FileText size={14} className="mr-1" /> Salva Note
                </Button>
              </div>
              {/* Audit Trail Timeline */}
              {detailAppt.status_history && detailAppt.status_history.length > 0 && (
                <div>
                  <Label className="mb-2 block">Storico Modifiche</Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {detailAppt.status_history.map((h, i) => (
                      <div key={`${h.new_status}-${h.timestamp}-${i}`} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-2.5 h-2.5 rounded-full mt-1" style={{backgroundColor: STATUS_MAP[h.new_status]?.color || '#94a3b8'}} />
                          {i < detailAppt.status_history.length - 1 && <div className="w-px flex-1 bg-[hsl(var(--border))] mt-0.5" />}
                        </div>
                        <div className="flex-1 pb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${STATUS_MAP[h.new_status]?.className} text-[10px] px-1.5 py-0`}>{STATUS_MAP[h.new_status]?.label || h.new_status}</Badge>
                            {h.actor_email && <span className="text-[10px] text-muted-foreground">{h.actor_email}</span>}
                            {h.actor_role && <span className="text-[10px] text-muted-foreground">({h.actor_role})</span>}
                          </div>
                          <p className="text-[10px] text-muted-foreground">{h.timestamp ? new Date(h.timestamp).toLocaleString('it-IT', {dateStyle:'short', timeStyle:'short'}) : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {detailAppt?.status === 'pending' && (
              <div className="flex gap-2">
                <Button onClick={() => updateStatus(detailAppt.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle size={14} className="mr-1" /> Conferma</Button>
                <Button onClick={() => updateStatus(detailAppt.id, 'rejected')} variant="destructive"><XCircle size={14} className="mr-1" /> Rifiuta</Button>
              </div>
            )}
            {detailAppt?.status === 'confirmed' && (
              <Button onClick={() => updateStatus(detailAppt.id, 'completed')} className="bg-blue-600 hover:bg-blue-700 text-white"><CheckCircle size={14} className="mr-1" /> Completa</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
